import {FC, useState} from "react";
import {App, Button, Flex, Form, FormProps, List, Popconfirm, Popover, theme, Typography} from "antd";
import {AirportWithTime, Event} from "../../../../types/domain.ts";
import {DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {TimeInput} from "../../../../components/TimeInput/TimeInput.tsx";
import dayjs, {Dayjs} from "dayjs";
import styles from './EventInfoContent.module.css';
import {eventsEventIdAirportsAirportIdPatch} from "../../../../api/events/eventsEventIdAirportsAirportIdPatch.ts";
import {eventsEventIdAirportsAirportIdDelete} from "../../../../api/events/eventsEventIdAirportsAirportIdDelete.ts";

type Props = {
    event: Event;
    airport: AirportWithTime;
    onUpdate: VoidFunction;
    disabled?: boolean;
}

type FormType = {
    from: Dayjs;
    to: Dayjs;
}

export const AirportListItem: FC<Props> = ({ event, airport, onUpdate, disabled }) => {
    const { message } = App.useApp();
    const { token } = theme.useToken();
    const [form] = Form.useForm<FormType>();

    const [deleteLoading, setDeleteLoading] = useState(false);
    const handleDelete = () => {
        setDeleteLoading(true);
        eventsEventIdAirportsAirportIdDelete(event.id, airport.airport.id).then(() => {
            void message.success('Аэропорт удалён');
            onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setDeleteLoading(false));
    }

    const [loading, setLoading] = useState(false);
    const handleFinish: FormProps<FormType>['onFinish'] = (values) => {
        setLoading(true);
        eventsEventIdAirportsAirportIdPatch(event.id, airport.airport.id, {
            from: values.from.format('HH:mm') || airport.from,
            to: values.to.format('HH:mm') || airport.to
        }).then(() => {
            void message.success('Сохранено');
            onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setLoading(false));
    }

    const content = (
        <Form<FormType>
            form={form}
            className={styles.form}
            initialValues={{
                from: dayjs(airport.from, 'HH:mm'),
                to: dayjs(airport.to, 'HH:mm')
            }}
            onFinish={handleFinish}
            preserve={false}
        >
            <Flex gap={4}>
                <Form.Item<FormType>
                    name="from"
                    rules={[{ required: true }]}
                >
                    <TimeInput
                        placeholder="От"
                    />
                </Form.Item>
                <Form.Item<FormType>
                    name="to"
                    rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                            validator(_, to: Dayjs) {
                                const from = getFieldValue('from');
                                if(from.isAfter(to)) return Promise.reject(new Error('Ошибка'));
                                return Promise.resolve();
                            }
                        })
                    ]}
                >
                    <TimeInput
                        placeholder="До"
                    />
                </Form.Item>
            </Flex>
            <Form.Item noStyle>
                <Button type="primary" block htmlType="submit" loading={loading}>Сохранить</Button>
            </Form.Item>
        </Form>
    )

    return (
        <List.Item className={styles.listItem}>
            <Flex gap={4}>
                <Typography.Text>{airport.airport.name} ({airport.from} - {airport.to})</Typography.Text>
                {!disabled && (
                    <>
                        <Popover content={content} trigger="click" placement="bottom">
                            <EditOutlined style={{ color: token.colorPrimary }}/>
                        </Popover>
                        <Popconfirm
                            title="Удалить аэропорт?"
                            onConfirm={handleDelete}
                            okButtonProps={{ loading: deleteLoading }}
                        >
                            <DeleteOutlined style={{ color: token.colorError }}/>
                        </Popconfirm>
                    </>
                )}
            </Flex>
        </List.Item>
    )
}